// API Configuration
// Centralized configuration for API endpoints and environment detection

/**
 * Environment detection utilities
 */
export const Environment = {
  /**
   * Check if running in development environment
   */
  isDevelopment(): boolean {
    return import.meta.env.DEV;
  },

  /**
   * Check if running in production environment
   */
  isProduction(): boolean {
    return import.meta.env.PROD;
  },

  /**
   * Check if running on localhost
   */
  isLocalhost(): boolean {
    if (typeof window === 'undefined') return false;
    
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname.includes('localhost') ||
      hostname.includes('127.0.0.1')
    );
  },

  /** Check if running on a hosted preview URL. */
  isPreview(): boolean {
    if (typeof window === 'undefined') return false;
    
    const hostname = window.location.hostname;
    return (
      hostname.includes('workers.dev') ||
      hostname.includes('preview')
    );
  },

  /**
   * Get current environment name
   */
  getEnvironment(): 'development' | 'preview' | 'production' {
    if (this.isDevelopment() || this.isLocalhost()) {
      return 'development';
    }
    if (this.isPreview()) {
      return 'preview';
    }
    return 'production';
  }
};

/**
 * API Configuration
 */
export const ApiConfig = {
  /**
   * Get the base API URL based on environment
   */
  getBaseUrl(): string {
    // The Cloudflare Worker serves the SPA and API from the same origin.
    return '';
  },

  /**
   * Get full API endpoint URL
   */
  getEndpoint(path: string): string {
    const baseUrl = this.getBaseUrl();
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  },

  /**
   * Get API configuration for fetch requests
   */
  getRequestConfig(): RequestInit {
    const isSecureContext = Environment.isProduction();
    
    return {
      headers: {
        'Content-Type': 'application/json',
        // Security headers for production
        ...(isSecureContext && {
          'X-Requested-With': 'XMLHttpRequest',
          'X-Content-Type-Options': 'nosniff',
          'X-Frame-Options': 'DENY'
        })
      },
      // Security settings for production
      ...(isSecureContext && {
        credentials: 'same-origin' as RequestCredentials,
        mode: 'cors' as RequestMode,
        cache: 'no-cache' as RequestCache
      }),
      // Development settings
      ...(!isSecureContext && {
        credentials: 'include' as RequestCredentials
      })
    };
  },

  /**
   * Validate API URL for security
   */
  validateUrl(url: string): boolean {
    try {
      const urlObj = new URL(url, window.location.origin);
      
      // In production, ensure HTTPS
      if (Environment.isProduction() && urlObj.protocol !== 'https:') {
        // Production API calls should use HTTPS
        return false;
      }
      
      if (Environment.isProduction() && urlObj.origin !== window.location.origin) {
        return false;
      }
      
      return true;
    } catch (error) {
      // Invalid URL
      return false;
    }
  },

  /**
   * Make a secure API request with validation
   */
  async secureRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const url = this.getEndpoint(endpoint);
    
    // Validate URL in production
    if (Environment.isProduction() && !this.validateUrl(url)) {
      throw new Error('Invalid or insecure API endpoint');
    }
    
    const config = {
      ...this.getRequestConfig(),
      ...options,
      headers: {
        ...this.getRequestConfig().headers,
        ...options.headers
      }
    };
    
    return fetch(url, config);
  }
};

/**
 * Export commonly used values
 */
// API_BASE_URL is deprecated - use ApiConfig.getEndpoint() instead
export const IS_DEVELOPMENT = Environment.isDevelopment();
export const IS_PRODUCTION = Environment.isProduction();
export const IS_LOCALHOST = Environment.isLocalhost();
